import type { TableActionGroup } from '@shared/types/data-table'
import type { Ecommerce } from '@features/ecommerce/types/ecommerce.types'

interface EcommerceRowActionsConfig {
  onViewDetails?: (ecommerce: Ecommerce) => void
  onUpdate?: (ecommerce: Ecommerce) => void
  onDuplicate?: (ecommerce: Ecommerce) => void
  onDeactivate?: (ecommerce: Ecommerce) => void
  onViewPagePreview?: (ecommerce: Ecommerce) => void
  onBulkActivate?: (ecommerceIds: string[]) => void
  onBulkDeactivate?: (ecommerceIds: string[]) => void
  onBulkExport?: (ecommerceIds: string[]) => void
}

// Row actions for tables - quick access operations
export function getEcommerceRowActions(config: EcommerceRowActionsConfig) {
  const actions = []

  if (config.onViewDetails) {
    actions.push({
      key: 'view-details',
      label: 'View Details',
      onClick: config.onViewDetails,
    })
  }

  if (config.onUpdate) {
    actions.push({
      key: 'update',
      label: 'Edit',
      onClick: config.onUpdate,
    })
  }

  if (config.onDuplicate) {
    actions.push({
      key: 'duplicate',
      label: 'Duplicate',
      onClick: config.onDuplicate,
    })
  }

  return actions
}

// Panel actions for detail pages - grouped to match design
export function getEcommercePanelActions(config: EcommerceRowActionsConfig): TableActionGroup<Ecommerce>[] {
  return [
    {
      label: 'Actions',
      actions: [
        {
          key: 'update-ecommerce',
          label: 'Update E-commerce',
          onClick: (ecommerce: Ecommerce) => config.onUpdate?.(ecommerce),
        },
        {
          key: 'deactivate-hosted-page',
          label: 'Deactivate Hosted Page',
          onClick: (ecommerce: Ecommerce) => config.onDeactivate?.(ecommerce),
          variant: 'destructive' as const,
          condition: (ecommerce: Ecommerce) => ecommerce.status === 'active',
        },
      ],
    },
    {
      label: 'Connections',
      actions: [
        {
          key: 'view-page-preview',
          label: 'View Page preview',
          onClick: (ecommerce: Ecommerce) => config.onViewPagePreview?.(ecommerce),
        },
      ],
    },
  ]
}

export function getEcommerceBulkActions(config: EcommerceRowActionsConfig = {}) {
  return [
    {
      key: 'activate',
      label: 'Activate Configurations',
      onClick: (ids: string[]) => config.onBulkActivate?.(ids),
      disabled: (ids: string[]) => ids.length === 0,
    },
    {
      key: 'deactivate',
      label: 'Deactivate Configurations',
      onClick: (ids: string[]) => config.onBulkDeactivate?.(ids),
      variant: 'destructive' as const,
      disabled: (ids: string[]) => ids.length === 0,
    },
    {
      key: 'export',
      label: 'Export Configurations',
      onClick: (ids: string[]) => config.onBulkExport?.(ids),
      disabled: (ids: string[]) => ids.length === 0,
    },
  ]
}
